/**
 * <docs-themepicker> -- theme switcher for the brio docs.
 *
 * Author provides a list of <button data-theme="..."> menu items inside
 * a <brio-dropdown>. The element wires click handling (scoped to its
 * own subtree), manages the theme override <link> appended to <head>,
 * and persists the selection to localStorage under "brio-theme".
 *
 * Markup:
 *   <docs-themepicker>
 *     <brio-dropdown>
 *       <button type="button" commandfor="theme-picker" command="toggle-popover">
 *         <span>Default</span>
 *       </button>
 *       <div class="popover dropdown" id="theme-picker" popover role="menu">
 *         <button class="dropdown-item" type="button" role="menuitem"
 *                 data-theme="default">Default</button>
 *         <button class="dropdown-item" type="button" role="menuitem"
 *                 data-theme="forest">Forest</button>
 *       </div>
 *     </brio-dropdown>
 *   </docs-themepicker>
 *
 * The "default" pseudo-theme removes any active override. Other values
 * resolve to assets/themes/{name}.css, built from src/css/themes/*.css
 * by `pnpm build`. Pages render at different depths (component pages
 * sit two levels down), so the themes directory is resolved from the
 * main stylesheet's own URL rather than a baked-in base path.
 *
 * The head boot script (docs/_partials/head.html) applies the saved
 * theme before first paint by appending the same link element -- both
 * sides manage the link by its id, so boot and picker never stack two
 * overrides.
 *
 * More than one picker can share a page (the navbar one plus an inline
 * one). Each syncs its own menu and trigger label; a brio:theme-change
 * event keeps the other instances' aria-current state in step.
 *
 * Scope: docs-only. Lives in src/docs/ and is registered from
 * src/docs/docs.js.
 */

import { BrioBase } from '../js/brio-base.js';

const STORAGE_KEY = 'brio-theme';
const OVERRIDE_LINK_ID = 'brio-theme-override';

function loadSavedTheme() {
    try {
        return localStorage.getItem(STORAGE_KEY) || 'default';
    } catch {
        return 'default';
    }
}

// The themes directory, relative to the document. Every page loads the
// main stylesheet at {{base}}assets/brio.css, so its resolved URL pins
// the base no matter how deep the page sits.
function themesPath() {
    const main = document.querySelector('link[rel="stylesheet"][href$="brio.css"]');
    if (!main) return 'assets/themes';
    return new URL('themes', main.href).href;
}

class DocsThemepicker extends BrioBase {
    static tag = 'docs-themepicker';
    static {
        customElements.define(DocsThemepicker.tag, DocsThemepicker);
    }

    #onClick;
    #onExternalChange;

    connectedCallback() {
        this.#onClick = (e) => {
            const btn = e.target.closest('button[data-theme]');
            if (!btn || !this.contains(btn)) return;
            this.#applyTheme(btn.dataset.theme);
        };
        this.addEventListener('click', this.#onClick);

        // Another picker instance on the page changed the theme; keep
        // this one's menu and trigger label in step.
        this.#onExternalChange = (e) => {
            if (e.target !== this) this.#syncActiveButton(e.detail.theme);
        };
        document.addEventListener('brio:theme-change', this.#onExternalChange);

        // Sync with whatever the head boot script applied (or
        // "default" if nothing is saved).
        this.#syncActiveButton(loadSavedTheme());
    }

    disconnectedCallback() {
        if (this.#onClick) this.removeEventListener('click', this.#onClick);
        if (this.#onExternalChange)
            document.removeEventListener('brio:theme-change', this.#onExternalChange);
    }

    #applyTheme(theme) {
        // Swap the override by replacing the <link>, and defer the swap
        // to its own task. iOS Safari deadlocks if a head stylesheet is
        // inserted in the same task as hidePopover -- the menu's click
        // close runs first in bubble order, so the picker's swap lands
        // exactly there, the sheet's request pends forever, and the
        // page stops responding until reload (reproduced in
        // iPhone-emulated WebKit; verified fixed by splitting tasks).
        // One task later the same insert is safe at any age of the
        // close, so no delay is needed beyond the task boundary.
        setTimeout(() => {
            document.getElementById(OVERRIDE_LINK_ID)?.remove();

            if (theme !== 'default') {
                const link = document.createElement('link');
                link.id = OVERRIDE_LINK_ID;
                link.rel = 'stylesheet';
                link.href = `${themesPath()}/${theme}.css`;
                document.head.appendChild(link);
            }
        });

        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            /* localStorage unavailable -- silent fallback */
        }

        this.#syncActiveButton(theme);
        this.emit('theme-change', { theme });
    }

    #syncActiveButton(active) {
        // Mark the active menu item with aria-current. dropdown.css
        // styles [aria-current="true"] as the current selection (P5:
        // ARIA over .is-* for interactive state), and brio-dropdown
        // focuses the current item when the menu opens.
        let activeLabel = '';
        this.querySelectorAll('button[data-theme]').forEach((btn) => {
            if (btn.dataset.theme === active) {
                btn.setAttribute('aria-current', 'true');
                activeLabel = btn.textContent.trim();
            } else {
                btn.removeAttribute('aria-current');
            }
        });

        // Mirror the active theme on the trigger so the current theme
        // reads without opening the menu. The trigger is the button
        // that owns commandfor (the popover invoker).
        const trigger = this.querySelector('button[commandfor]');
        if (trigger && activeLabel) {
            const span = trigger.querySelector('span');
            if (span) {
                span.textContent = activeLabel;
            } else {
                trigger.textContent = activeLabel;
            }
        }
    }
}
